import React, { useState, forwardRef, useImperativeHandle } from "react";

import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide
} from "@material-ui/core";

import { Close as CloseIcon } from "@material-ui/icons";
import { TransitionProps } from "@material-ui/core/transitions";
import { DocusealForm } from '@docuseal/react';
import { useMutation } from "@tanstack/react-query";

import { signDocument } from "./queries";
import { useStyles } from "./styles";

import { useSubscription } from "../../../../../framework/src/contexts/Subscription";

const DOCUSEAL_BASE_URL = window._env_ ? window._env_.REACT_APP_DOCUSEAL_BASE_URL : 'localhost';

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export type SignatureDialogProps = {
  onSignSuccess: () => void;
};

export type SubmittedDocument = {
  surveyId: number,
  documentId: number,
  slug: string,
  token: string,
  title: string
}

const SignatureDialog = forwardRef(({ onSignSuccess }: SignatureDialogProps, ref) => {
  const [open, setOpen] = useState<boolean>(false);
  const [document, setDocument] = useState<SubmittedDocument>();
  const classes = useStyles();
  
  const signMutation = useMutation({
    mutationFn: async (signatureData: any) => {
      return signDocument(signatureData.document_id, signatureData);
    },
    onSuccess: (data) => {
      onSignSuccess();
    },
    onSettled: () => {
      setOpen(false);
    }
  });

  useImperativeHandle(ref, () => ({
    show() {
      setOpen(true);
    },
    hide() {
      setOpen(false);
    },
    setDocument(surveyId: number, documentId: number, title: string, token: string, slug: string) {
      setDocument({
        surveyId: surveyId,
        documentId: documentId,
        title: title,
        token: token,
        slug: slug
      });
    }
  }));

  return (
    <div>
      <Dialog
        fullScreen
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
      >
        <AppBar className={classes.appBar}>
          <Toolbar>
            <Typography variant="h6" className={classes.title}>
              {document?.title}
            </Typography>
            <IconButton
              edge="end"
              color="default"
              onClick={() => setOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        {document?.token && (
          <DocusealForm
            src={`https://${DOCUSEAL_BASE_URL}/s/${document.slug}`}
            token={document?.token}
            onComplete={(data) => {
              signMutation.mutate({ ...data, survey_id: document.surveyId, document_id: document.documentId});
            }}
            allowToResubmit={false}
            withTitle={false}
            sendCopyEmail={false}
            withSendCopyButton={false}
            rememberSignature={true}
          />
        )}
      </Dialog>
    </div>
  );
});

export default SignatureDialog;
